import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import styled from 'styled-components';
import { useModalContext } from '../../../context/ModalContext';
import CloseIcon from '@material-ui/icons/Close';
import BusinessIcon from '@material-ui/icons/Business';
import {
    AppUserAccount,
    useAppOrgListQuery,
    useAppUserAccountChangeOrg,
} from '../../../types/generated';

interface IDisguiseDialog {
    user: Pick<AppUserAccount, 'id' | 'email' | 'orgId' | 'orgName' | 'organizationId'>;
    organizationId: string;
}

interface ICurrentOrganization {
    id: string;
    businessName: string;
    organizationId: string;
}

const SC = {
    TitleWrapper: styled.div`
        display: flex;
        align-items: center;
    `,
    RowSpaceBetween: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    DialogContent: styled(DialogContent)`
        @media screen and (min-width: 768px) {
            max-width: 420px;
        }
    `,
    DialogContentText: styled(DialogContentText)`
        color: red;
    `,
    CloseButton: styled(IconButton)`
        display: none;

        @media screen and (min-width: 768px) {
            display: flex;
            width: 32px;
            height: 32px;
            margin: 0 16px;
        }
    `,
    SizedBox: styled.div`
        height: 8px;
    `,
    DialogActions: styled(DialogActions)`
        margin-right: 8px;
    `,
    BusinessIcon: styled(BusinessIcon)`
        margin-right: 4px;
    `,
};

export const DisguiseDialog = (props: IDisguiseDialog) => {
    const { user, organizationId } = props;
    const { visible, setVisible } = useModalContext();

    const handleClose = () => {
        setVisible(false);
    };

    // Get list of all organizations
    const { data: appOrganizations, loading } = useAppOrgListQuery();

    // Change organization (for admin users only)
    const [appUserAccountChangeOrg] = useAppUserAccountChangeOrg();

    const [currentOrganization, setCurrentOrganization] = useState<ICurrentOrganization>();

    // Reset organization to current, for example on backdrop click and dialog close button
    const resetCurrentOrganization = () =>
        setCurrentOrganization({
            id: user.orgId,
            businessName: user.orgName,
            organizationId: user.organizationId,
        });

    // Initiallize current organization select field
    useEffect(() => {
        setCurrentOrganization({
            id: user.orgId,
            businessName: user.orgName,
            organizationId: user.organizationId,
        });
    }, [user]);

    if (loading) {
        return <div>loading</div>;
    }

    return (
        <Dialog
            open={visible}
            onClose={() => {
                handleClose();
                resetCurrentOrganization();
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <SC.RowSpaceBetween>
                <DialogTitle id="alert-dialog-title">
                    <SC.TitleWrapper>
                        <SC.BusinessIcon />
                        Disguise as organization
                    </SC.TitleWrapper>
                </DialogTitle>
                <SC.CloseButton
                    onClick={() => {
                        handleClose();
                        resetCurrentOrganization();
                    }}
                >
                    <CloseIcon color="primary" />
                </SC.CloseButton>
            </SC.RowSpaceBetween>

            <SC.DialogContent>
                <SC.DialogContentText id="alert-dialog-description">
                    <Typography>
                        By choosing CONFIRM, you'll change the current organization associated with
                        your Bókun App Store account.
                    </Typography>
                    <SC.SizedBox />
                    <span>
                        <Typography>{`Current organization for ${user.email} `}</Typography>
                        <Typography>
                            <b>
                                ({organizationId}) {user.orgName}
                            </b>
                        </Typography>
                    </span>
                </SC.DialogContentText>

                <SC.SizedBox />

                <Autocomplete
                    id="combo-box-demo"
                    onChange={(event, value: ICurrentOrganization) => setCurrentOrganization(value)}
                    options={appOrganizations.GetOrganizations}
                    getOptionLabel={option =>
                        option.organizationId
                            ? `(${option.organizationId}) ${
                                  option.businessName ? option.businessName : option.supportEmail
                              }`
                            : ''
                    }
                    renderInput={params => (
                        <TextField {...params} label="Organization" variant="outlined" />
                    )}
                    blurOnSelect={true}
                    defaultValue={currentOrganization}
                />
            </SC.DialogContent>
            <SC.DialogActions>
                <SC.RowSpaceBetween>
                    <Button
                        onClick={() => {
                            handleClose();
                            resetCurrentOrganization();
                        }}
                        color="primary"
                    >
                        CANCEL
                    </Button>
                    <Button
                        onClick={() => {
                            handleClose();
                            appUserAccountChangeOrg({ variables: { id: currentOrganization?.id } });
                            window.location.reload();
                        }}
                        autoFocus
                        color="primary"
                    >
                        CONFIRM
                    </Button>
                </SC.RowSpaceBetween>
            </SC.DialogActions>
        </Dialog>
    );
};
